/**
 * NewAgentModeModal — escolha do modo de criação de um agente ADICIONAL.
 *
 * Antes, "Adicionar agente" jogava direto no onboarding. Agora o dono
 * escolhe (2026-08-05):
 *   • Modo guiado  → entrevista do onboarding (?additional=1), como sempre.
 *   • Criar do zero → só o nome; cria o agente vazio via /agents/onboarding
 *     (sections em branco, as_additional) e abre a tela Treinar agente com
 *     as abas para preencher direto — sem passar pelo onboarding.
 */
import React, { useState } from 'react';
import { X, Sparkles, PenLine, Loader2, ArrowRight, Bot } from 'lucide-react';
import { createAgentFromOnboarding } from '../../services/agents/agentService';

const NewAgentModeModal = ({ isOpen, onClose, onGuided, onCreatedFromScratch, colors }) => {
  const [mode, setMode] = useState(null); // null | 'scratch'
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const reset = () => {
    setMode(null);
    setName('');
    setError(null);
  };

  const handleClose = () => {
    if (creating) return;
    reset();
    onClose?.();
  };

  const handleCreateFromScratch = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2 || creating) return;
    setCreating(true);
    setError(null);
    try {
      const result = await createAgentFromOnboarding({
        name: trimmed,
        description: '',
        business_category: 'geral',
        sections: { faq: { items: [] } },
        as_additional: true,
      });
      const agentId = result?.data?.agent?.id;
      if (!agentId) {
        throw new Error('Resposta sem o id do agente');
      }
      reset();
      onCreatedFromScratch?.(agentId);
    } catch (err) {
      console.error('[NewAgentModeModal] create error:', err);
      setError('Não foi possível criar o agente. Tente novamente.');
    } finally {
      setCreating(false);
    }
  };

  const cardStyle = (active) => ({
    display: 'flex', gap: 14, alignItems: 'flex-start', textAlign: 'left',
    padding: 16, width: '100%',
    backgroundColor: active ? `${colors.accentPrimary}10` : colors.bgSecondary,
    border: `1.5px solid ${active ? colors.accentPrimary : colors.borderColor}`,
    borderRadius: 14, cursor: 'pointer', transition: 'all 0.15s ease',
  });

  const iconBox = (bg, fg) => ({
    width: 42, height: 42, borderRadius: 12, flexShrink: 0,
    backgroundColor: bg, color: fg,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  });

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        style={{
          width: '100%', maxWidth: 480,
          backgroundColor: colors.bgPrimary,
          borderRadius: 16,
          border: `1px solid ${colors.borderColor}`,
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 22px', borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={iconBox(`${colors.accentPrimary}18`, colors.accentPrimary)}>
              <Bot size={20} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>
                Novo agente
              </div>
              <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 1 }}>
                Como você quer criar?
              </div>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 6, color: colors.textTertiary, borderRadius: 8,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button type="button" style={cardStyle(false)} onClick={onGuided}>
            <div style={iconBox(`${colors.accentPrimary}18`, colors.accentPrimary)}>
              <Sparkles size={20} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
                  Modo guiado
                </span>
                <span style={{
                  fontSize: 10, fontWeight: 700, color: colors.accentPrimary,
                  backgroundColor: `${colors.accentPrimary}22`,
                  padding: '2px 8px', borderRadius: 999,
                }}>
                  Recomendado
                </span>
              </div>
              <div style={{ fontSize: 12.5, color: colors.textSecondary, marginTop: 4, lineHeight: 1.5 }}>
                Uma entrevista rápida em 6 etapas monta o agente para você:
                identidade, tom de voz, regras e conhecimentos. Dá para
                responder por áudio.
              </div>
            </div>
            <ArrowRight size={16} color={colors.textTertiary} style={{ alignSelf: 'center', flexShrink: 0 }} />
          </button>

          <button
            type="button"
            style={cardStyle(mode === 'scratch')}
            onClick={() => setMode('scratch')}
          >
            <div style={iconBox(
              mode === 'scratch' ? `${colors.accentPrimary}18` : colors.bgPrimary,
              mode === 'scratch' ? colors.accentPrimary : colors.textSecondary
            )}>
              <PenLine size={20} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
                Criar do zero
              </span>
              <div style={{ fontSize: 12.5, color: colors.textSecondary, marginTop: 4, lineHeight: 1.5 }}>
                Você dá o nome e preenche as seções do treinamento direto na
                tela do agente, sem passar pela entrevista.
              </div>
            </div>
          </button>

          {mode === 'scratch' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFromScratch(); }}
                placeholder="Nome do agente (ex: Sofia)"
                maxLength={60}
                disabled={creating}
                style={{
                  flex: 1, padding: '11px 14px',
                  backgroundColor: colors.bgSecondary,
                  border: `1.5px solid ${colors.borderColor}`,
                  borderRadius: 10, fontSize: 14,
                  color: colors.textPrimary, outline: 'none',
                }}
              />
              <button
                onClick={handleCreateFromScratch}
                disabled={name.trim().length < 2 || creating}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '11px 18px', borderRadius: 10, border: 'none',
                  fontSize: 13, fontWeight: 600,
                  cursor: name.trim().length >= 2 && !creating ? 'pointer' : 'not-allowed',
                  backgroundColor: name.trim().length >= 2 && !creating
                    ? colors.accentPrimary : colors.borderColor,
                  color: name.trim().length >= 2 && !creating
                    ? '#fff' : colors.textSecondary,
                  flexShrink: 0,
                }}
              >
                {creating
                  ? (<><Loader2 size={14} style={{ animation: 'nam-spin 1s linear infinite' }} /> Criando…</>)
                  : 'Criar agente'}
              </button>
            </div>
          )}

          {error && (
            <div style={{
              padding: '9px 12px', borderRadius: 8, fontSize: 12,
              backgroundColor: '#DC262615', color: '#DC2626',
            }}>
              {error}
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes nam-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default NewAgentModeModal;
